import React, { useState, lazy, Suspense } from 'react';
import {
  Box,
  CssBaseline,
  Toolbar,
  Container,
  Typography,
  Paper,
  Alert,
  Fade,
  CircularProgress,
  Chip,
} from '@mui/material';
import Navbar from '../Layout/Navbar';
import Sidebar from '../Layout/Sidebar';

// Lazy load components
const Overview = lazy(() => import('../Overview'));
const Overview2 = lazy(() => import('../Overview/Overview2'));
const RealtimeOverview = lazy(() => import('../Realtime/Overview'));
const CallbackManagement = lazy(() => import('../Callback/CallbackManagement'));
const AgentPerformance = lazy(() => import('../Agent/AgentPerformance'));
const TransferAnalysis = lazy(() => import('../Transfer/TransferAnalysis'));
const ExcludedNumbers = lazy(() => import('../ExcludedNumbers/ExcludedNumbers'));
const SmartClinicReport = lazy(() => import('../Report'));
const SmartClinicFTRReport = lazy(() => import('../Report/SmartClinicFTRReport'));
const SmartClinicFTRReportV2 = lazy(() => import('../Report/SmartClinicFTRReportV2'));
const SmartClinicDIDDashboard = lazy(() => import('./SmartClinicDIDDashboardAdvanced'));
const CalledNumbers = lazy(() => import('../CalledNumbers/CalledNumbers'));
const CalledNumbersV2 = lazy(() => import('../CalledNumbersV2/CalledNumbersV2'));
const CDRLive = lazy(() => import('../CDR/CDRLive'));
const QueueStatus = lazy(() => import('../Queue/QueueStatus'));
const ActiveCallsFixed = lazy(() => import('../ActiveCalls/ActiveCallsFixed'));
const CallbackAnalytics = lazy(() => import('../Callback/CallbackAnalytics'));
const IVRStats = lazy(() => import('../IVR/IVRStats'));
const CallbackBasic = lazy(() => import('../CallbackBasic/CallbackBasic'));
const CallbackAdvanced = lazy(() => import('../Callback/CallbackAdvanced'));
const InoltriStruttura = lazy(() => import('../InoltriStruttura'));
const AutomatedRecallMonitor = lazy(() => import('../AutomatedRecall/AutomatedRecallMonitor'));

const MainDashboard: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentSection, setCurrentSection] = useState('overview');

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const getSectionTitle = (section: string): string => {
    const titles: Record<string, string | undefined> = {
      // Overview
      'overview': 'Overview Dashboard',
      'overview2': 'Overview Dashboard V2',
      'callback-basic': 'CallBack Basic',
      'callback-advanced': 'CallBack Advanced',
      
      // Real-time sections
      'realtime-overview': 'Real-time Overview',
      'cdr': 'CDR Live',
      'queues': 'Queue Status',
      'agents': 'Agent Status',
      'active-calls': 'Active Calls',
      'transfers': 'Transfers',
      'callbacks': 'Callback Analytics',
      'called-numbers': undefined,
      'called-numbers-v2': 'Numeri Chiamati V2',
      'ivr-stats': 'IVR Statistics',
      'inoltri-struttura': 'Inoltri in Struttura',
      'automated-recall': 'Recall Automatico V2',
      
      // Historical sections
      'historical-calls': 'Historical Calls',
      'historical-agents': 'Agent Performance',
      'historical-queues': 'Historical Queue Analysis',
      
      // Reports sections
      'smartclinic-report': 'Report Mensile SmartClinic',
      'smartclinic-ftr-report': 'SmartClinic FTR (Filter by DID)',
      'smartclinic-ftr-v2-report': 'SmartClinic FTR V2 (Code 511-550)',
      'smartclinic-did': 'SmartClinic DID Dashboard',
      'custom-reports': 'Custom Reports',
      'scheduled-reports': 'Scheduled Reports',
      
      // Recall sections
      'recall-list': 'Recall Management',
      'recall-campaigns': 'Recall Campaigns',
      'recall-analytics': 'Recall Analytics',
      'excluded-numbers': 'Numeri Esclusi',
      
      // System sections
      'system-health': 'System Health',
      'system-users': 'User Management',
      'system-settings': 'System Settings',
    };
    
    return titles[section] || '';
  };

  const renderSection = () => {
    // Render specific component based on current section
    switch (currentSection) {
      case 'overview':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <Overview />
          </Suspense>
        );
      
      case 'overview2':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <Overview2 />
          </Suspense>
        );
      
      case 'callback-basic':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <CallbackBasic />
          </Suspense>
        );
      
      case 'callback-advanced':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <CallbackAdvanced />
          </Suspense>
        );
      
      
      case 'realtime-overview':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <RealtimeOverview />
          </Suspense>
        );
      
      case 'recall-list':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <CallbackManagement />
          </Suspense>
        );
      
      case 'agents':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <AgentPerformance />
          </Suspense>
        );
      
      case 'transfers':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <TransferAnalysis />
          </Suspense>
        );
      
      case 'excluded-numbers':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <ExcludedNumbers />
          </Suspense>
        );
      
      case 'smartclinic-report':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <SmartClinicReport />
          </Suspense>
        );
      
      case 'smartclinic-ftr-report':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <SmartClinicFTRReport />
          </Suspense>
        );

      case 'smartclinic-ftr-v2-report':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <SmartClinicFTRReportV2 />
          </Suspense>
        );

      case 'smartclinic-did':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <SmartClinicDIDDashboard />
          </Suspense>
        );
      
      case 'called-numbers':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <CalledNumbers />
          </Suspense>
        );
      
      case 'called-numbers-v2':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <CalledNumbersV2 />
          </Suspense>
        );
      
      case 'cdr':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <CDRLive />
          </Suspense>
        );
      
      case 'queues':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <QueueStatus />
          </Suspense>
        );
      
      case 'active-calls':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <ActiveCallsFixed />
          </Suspense>
        );
      
      case 'callbacks':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <CallbackAnalytics />
          </Suspense>
        );
      
      case 'ivr-stats':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <IVRStats />
          </Suspense>
        );
      
      case 'inoltri-struttura':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <InoltriStruttura />
          </Suspense>
        );
      
      case 'automated-recall':
        return (
          <Suspense fallback={
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          }>
            <AutomatedRecallMonitor />
          </Suspense>
        );
      
      default:
        return (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              {getSectionTitle(currentSection)}
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              This section is ready for implementation
            </Alert>
          </Paper>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* Navigation Bar */}
      <Navbar
        onMenuToggle={handleSidebarToggle}
        title="BiSolution Analytics"
      />
      
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentSection={currentSection}
        onSectionChange={setCurrentSection}
      />
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          transition: (theme) => theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: { xs: 0, md: sidebarOpen ? 0 : '-260px' },
          height: '100vh',
          overflow: 'auto',
        }}
        className="custom-scroll"
      >
        <Toolbar />
        
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Section Header */}
          {currentSection !== 'called-numbers' && getSectionTitle(currentSection) && (
            <Box mb={3} display="flex" justifyContent="space-between" alignItems="center"
              sx={{ animation: 'fadeInUp 400ms ease forwards' }}>
              <Typography variant="h4" component="h1"
                sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                {getSectionTitle(currentSection)}
              </Typography>
              {(currentSection === 'overview' || currentSection === 'overview2') && (
                <Chip label="LIVE" size="small"
                  sx={{ backgroundColor: 'rgba(16,185,129,0.12)', color: '#34D399', border: '1px solid rgba(16,185,129,0.24)', fontWeight: 600,
                    '&::before': { content: '""', display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#10B981', marginRight: 1, animation: 'pulse 2s ease-in-out infinite' } }} />
              )}
            </Box>
          )}

          {/* Section Content */}
          <Fade in timeout={300}>
            <Box>
              {renderSection()}
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default MainDashboard;